// ─────────────────────────────────────────────────────────────────────────────
// useDomainSurface — the feed behind a declaration-driven domain surface.
// Shared core (owner ruling 2026-07-31, "one core, every window").
//
// THE WHOLE POINT: this hook names no domain, no agent and no root. It is handed
// a domain key and returns what the OS DECLARES about it —
//
//   • roots   ← /api/os/structure  → the board surface's `tabs` (v4 contract §6.3,
//               the sender Forge closed in 6ad039d9). The nine GROWTH roots are a
//               declaration, not a constant; a tenth arrives here with no edit.
//   • roster  ← /api/os/structure  → agents whose domainKey is this domain. The
//               registry (agents_for_domain) is the single source; a component
//               listing known agents would pass over the one added next week.
//   • board   ← /api/domain-board/<domain> → the store, divided by root slug.
//
// READ-ONLY, STRUCTURALLY. Both endpoints are GET. The board's PUT twin is the
// sole writer server-side and is not reachable from here — endpoints.js does not
// export it. A RENDER IS NOT A MUTATION.
//
// ABSENT IS NOT BROKEN. The board store does not exist until the first PUT, and
// GET answers {"board": {}} for it. That is the CORRECT answer and it arrives as
// `board: {}` with `err: null` — never an error state. Only a transport or auth
// failure sets `err`.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import { authHeaders, POLL_MS } from './authHeaders';
import { OS_STRUCTURE, domainBoard } from './endpoints';
import { boardEventApplies, subscribeBoardChanged } from './boardEvents';

/** Mirrors core/harness_topology.slug EXACTLY — the one derivation, restated in TS.
 *  Display labels are free text; the slug is what identity is carried as, and it
 *  is what keys the board. Divergence here would silently point a panel at a key
 *  the server never writes, so this stays byte-faithful to the Python:
 *  lowercase → keep alnum → any run of non-alnum becomes ONE underscore → strip. */
export function slug(label: string): string {
  const out: string[] = [];
  let prevUs = false;
  for (const ch of String(label).trim().toLowerCase()) {
    if (/[a-z0-9]/.test(ch)) { out.push(ch); prevUs = false; }
    else if (!prevUs) { out.push('_'); prevUs = true; }
  }
  return out.join('').replace(/^_+|_+$/g, '');
}

export interface StructureAgent {
  id: string; name: string; label: string; kind: string;
  domainId?: string; domainKey?: string; teamId?: string;
  crossDomain?: boolean; implementationState: string;
  /**
   * IS THIS AGENT AUTONOMOUS? THREE-VALUED, and the third value is the point.
   *
   * Joined producer-side in `core/os_structure.build_os_structure` from ONE
   * `loop_toggle.table()` read for the whole projection — never per agent: the
   * per-agent route costs 2653 ms of hub-blocking for Buck, and a roster asking
   * per row is that many. `null` is "nobody has ruled, or the toggle could not
   * be read" and is NEVER collapsed to false; the roster renders it grey.
   */
  autonomous?: boolean | null;
  /** DECLARED SHAPE (core/os_structure.py, owner ruling 2026-08-19).
   *  `role` is {id, label} — the id is identity and never renames, the label is
   *  the job title and is presentation. NULL where the agent declares none, and
   *  never defaulted: a stand-in would be indistinguishable from a declaration. */
  role?: { id: string; label: string } | null;
  /** The entity id of the ONE admin a receptionist attaches to, joined against
   *  the same agents[] list. NULL where no pairing is declared — which is the
   *  case for every agent today, and the rail's ↳ slot renders empty for it. */
  attachedToId?: string | null;
}
interface StructureSurface {
  id: string; label: string; ownerId?: string; ownerKind?: string;
  surfaceKind?: string; backingStoreExists?: boolean | null;
  tabs?: string[] | null; implementationState: string;
}
interface StructureDomain { id: string; key: string; label: string; }

/** One declared root: the label a human reads, and the slug identity is carried as. */
export interface DomainRoot { label: string; slug: string; }

export type DomainBoard = Record<string, unknown>;

export interface DomainSurfaceFeed {
  /** Declared roots in declaration order. Empty = this domain declares none. */
  roots: DomainRoot[];
  /** The domain's own declared label, for the brand block. Falls back to the key
   *  it was handed — a surface must never go blank because a label is missing. */
  domainLabel: string;
  /** Domain-native agents from the registry projection, name-sorted. */
  roster: StructureAgent[];
  /** EVERY agent the projection returned, name-sorted — including cross-domain
   *  service and code-team agents, which belong to no single domain. The rail
   *  surfaces them in their own group; unreachable is worse than out of place. */
  allAgents: StructureAgent[];
  /** Resolve an attachment target by entity id. Joins against the FULL projection,
   *  not the domain-native roster, so an attachment is readable wherever it points.
   *  Returns null when nothing is declared or the id does not resolve — the caller
   *  renders the empty slot, never a guess. */
  agentById: (id: string | null | undefined) => StructureAgent | null;
  /** The board, keyed by root slug. `{}` when the store is absent — CORRECT, not an error. */
  board: DomainBoard;
  /** THE COMPANY THE HELD BOARD WAS READ FOR — `?company=` on the board read,
   *  or null. A selection change starts a new read while the previous answer is
   *  still held; a reader of a per-company root compares this against the
   *  selection and refuses to paint an answer that belongs to another company. */
  boardFor: string | null;
  /** RE-READ THE BOARD NOW — for a write this surface just made (the call
   *  gate), so the list moves at once instead of waiting out the poll. The
   *  same read the poll and the board event use, so the superseded-answer
   *  check still guards it. A no-op before the first effect has run. */
  refresh: () => void;
  /** True once BOTH reads have answered at least once. */
  ready: boolean;
  /** Transport/auth failure ONLY. An absent store is not an error. */
  err: string | null;
}

/**
 * `company` IS THE PER-COMPANY READ (`GET /api/domain-board/<domain>?company=`).
 * A domain whose board route does not know the parameter ignores it. It rides a
 * ref so the 30 s poll always asks for the CURRENT selection, and a change asks
 * at once rather than waiting out the poll — without blanking the surface: the
 * previous board stays held, stamped with `boardFor`, until the new one lands.
 */
export function useDomainSurface(domain: string, company: string | null = null): DomainSurfaceFeed {
  const [surfaces, setSurfaces] = useState<StructureSurface[] | null>(null);
  const [agents, setAgents] = useState<StructureAgent[] | null>(null);
  const [domains, setDomains] = useState<StructureDomain[] | null>(null);
  const [board, setBoard] = useState<DomainBoard | null>(null);
  const [boardFor, setBoardFor] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const companyRef = useRef<string | null>(company);
  const readBoardRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    let live = true;
    setSurfaces(null); setAgents(null); setDomains(null); setBoard(null); setBoardFor(null); setErr(null);

    const readBoard = () => {
      const asked = companyRef.current;
      const url = domainBoard(domain) + (asked ? '?company=' + encodeURIComponent(asked) : '');
      fetch(url, { headers: authHeaders() })
        .then((r) => r.json())
        .then((j) => {
          if (!live) return;
          // A SUPERSEDED ANSWER IS DROPPED. The selection moved while this read
          // was in flight; painting it would put one company's root under
          // another's name for up to a whole poll.
          if (asked !== companyRef.current) return;
          // {"board": {}} is the ABSENT store answering correctly — an empty
          // object, never an error. Only `success: false` is a real failure.
          if (j && j.success) { setBoard((j.board as DomainBoard) || {}); setBoardFor(asked); }
          else setErr((j && j.error) || 'board unavailable');
        })
        .catch((e) => { if (live) setErr(String(e)); });
    };
    readBoardRef.current = readBoard;

    const read = () => {
      fetch(OS_STRUCTURE, { headers: authHeaders() })
        .then((r) => r.json())
        .then((j) => {
          if (!live) return;
          if (j && j.success && j.structure) {
            setSurfaces(j.structure.surfaces || []);
            setAgents(j.structure.agents || []);
            setDomains(j.structure.domains || []);
          } else setErr((j && j.error) || 'structure unavailable');
        })
        .catch((e) => { if (live) setErr(String(e)); });

      readBoard();
    };

    read();
    const poll = setInterval(read, POLL_MS);
    // ── A BOARD WRITE RE-READS AT ONCE (owner ruling 2026-09-15) ────────────
    // The poll stays as the floor; the event is what makes the screen keep up.
    // The selection is read at EVENT time from the ref, so an event for a
    // company the owner has since moved away from is ignored — and a read it
    // did start is still dropped by the superseded-answer check above.
    const unsubscribe = subscribeBoardChanged((event) => {
      if (live && boardEventApplies(event, domain, companyRef.current)) readBoard();
    });
    return () => { live = false; readBoardRef.current = null; clearInterval(poll); unsubscribe(); };
  }, [domain]);

  // A NEW SELECTION ASKS NOW. The first render's value is already in the ref and
  // the domain effect reads it, so this fires only on a CHANGE.
  useEffect(() => {
    if (companyRef.current === company) return;
    companyRef.current = company;
    if (readBoardRef.current) readBoardRef.current();
  }, [company]);

  const roots = useMemo<DomainRoot[]>(() => {
    if (!surfaces || !domains) return [];
    const dom = domains.find((d) => d.key === domain);
    if (!dom) return [];
    // The domain's OWN board surface — matched by ownership, never by an id string
    // this file builds. `tabs` is honestly absent where no roots are declared.
    const board_ = surfaces.find((s) => s.ownerId === dom.id && Array.isArray(s.tabs) && s.tabs.length > 0);
    return (board_?.tabs || []).map((label) => ({ label, slug: slug(label) }));
  }, [surfaces, domains, domain]);

  // The domain's DECLARED label — read from the registry projection, never a
  // literal. Degrades to the key so the brand line always says something true.
  const domainLabel = useMemo<string>(() => {
    const dom = (domains || []).find((d) => d.key === domain);
    return dom?.label || domain;
  }, [domains, domain]);

  const roster = useMemo<StructureAgent[]>(() => {
    if (!agents) return [];
    return agents
      .filter((a) => (a.domainKey || '').toUpperCase() === domain.toUpperCase() && !a.crossDomain)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [agents, domain]);

  const allAgents = useMemo<StructureAgent[]>(
    () => (agents || []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [agents]);

  const agentById = useMemo(() => {
    const by = new Map((agents || []).map((a) => [a.id, a]));
    return (id: string | null | undefined) => (id ? by.get(id) || null : null);
  }, [agents]);

  const refresh = useMemo(() => () => { if (readBoardRef.current) readBoardRef.current(); }, []);

  return {
    roots,
    domainLabel,
    roster,
    allAgents,
    agentById,
    board: board || {},
    boardFor,
    refresh,
    ready: surfaces !== null && agents !== null && board !== null,
    err,
  };
}
